import moment from "moment-timezone";
import { CSSProperties } from "react";
import { Column, flexRender } from '@tanstack/react-table'
import { ICurrency, ITransformedData, YearlyData } from "../interfaces/ReimbursementLimit";
import { PaymentTermin, TempPaymentTermin } from "../interfaces/Termin/Termin";
import { currencylocales } from "../interfaces/Options";
import { isPhoneNumber } from "./chat";
import { CurrencyCodes } from "../interfaces/Currency";
import { SettingMultiPipelines } from "../interfaces/Settings";
import axios from "axios";
import { Pipeline } from "../interfaces";
import { ListProduct } from "../interfaces";
import { ChatGroupRoom, ChatRoom, ListChatGroupParticipants, ListChatGroupRoom } from "../interfaces/Chat/Chat";

export function customNumberFormat(
  valueNumber: number,
  thousandSeparator: string = '.',
  decimalSeparator: string = ','
): string {
  const [integerPart, decimalPart] = valueNumber.toString().split('.') // Pisahkan bilangan bulat dan desimal
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator) // Format ribuan

  // Jika ada desimal, tambahkan ke hasil, jika tidak cukup bilangan bulat saja
  return decimalPart ? `${formattedInteger}${decimalSeparator}${decimalPart}` : formattedInteger
}

export function inputNumberFormat(valueString: string): string {
  const parts = valueString.split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  return parts.join('.')
}

export function secondsToTimeFormat(totalSeconds: number) {
  const hours = Math.floor(totalSeconds / 3600)
  const remainingSeconds = totalSeconds % 3600
  const minutes = Math.floor(remainingSeconds / 60)
  const seconds = remainingSeconds % 60

  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`
  } else {
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }
}

export function convertSecondsToMinutes(seconds: number): number {
  return seconds / 60;
}

export function convertMinutesToSeconds(minutes: number): number {
  return minutes * 60;
}

export const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0') // +1 because months are 0-indexed in JavaScript
  const year = date.getFullYear()

  return `${day}-${month}-${year}`
}

export const formatDateDataTable = (dateString: Date) => {
  const date = new Date(dateString)
  const year = date.getFullYear()
  // JavaScript's getMonth() returns 0 for January, 1 for February, etc.
  // So, we add 1 to get the actual month number.
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

export function formatSecondsToTime(seconds: number): string {
  const days = Math.floor(seconds / (24 * 60 * 60));
  const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
  const minutes = Math.floor((seconds % (60 * 60)) / 60);
  const remainingSeconds = seconds % 60;

  const parts = [];
  if (days > 0) parts.push(`${days} hari`);
  if (hours > 0) parts.push(`${hours} jam`);
  if (minutes > 0) parts.push(`${minutes} menit`);
  if (remainingSeconds > 0) parts.push(`${remainingSeconds} detik`);

  if (parts.length === 0) return '-'
  else return parts.join(', ');
}

export const getDateDifference = (startDate: string, endDate: string) => {
  const start = new Date(startDate)
  const end = new Date(endDate)

  let differenceInYears = end.getFullYear() - start.getFullYear()
  let differenceInMonths = end.getMonth() - start.getMonth()
  let differenceInDays = end.getDate() - start.getDate()

  if (differenceInDays < 0) {
    differenceInDays += new Date(end.getFullYear(), end.getMonth(), 0).getDate() // Days in last month
    differenceInMonths--
  }

  if (differenceInMonths < 0) {
    differenceInMonths += 12
    differenceInYears--
  }

  let displayDifference = ''
  if (differenceInYears > 0) {
    return `> 1 Tahun `
  }

  if (differenceInMonths > 0) {
    displayDifference += `${differenceInMonths} Bulan `
  }

  if (differenceInDays > 0) {
    displayDifference += `${differenceInDays} Hari`
  }

  if (differenceInYears === 0 && differenceInMonths === 0 && differenceInDays === 0) {
    displayDifference = '0 Hari'
  }

  return displayDifference
}

export const getDateRangeDuration = (days: string, nextDays: string): string => {
  let displayDuration = ''

  const date1 = new Date(days)
  const date2 = new Date(nextDays)

  // Menghitung perbedaan dalam tahun, bulan, dan hari
  let years = date2.getFullYear() - date1.getFullYear()
  let months = date2.getMonth() - date1.getMonth()
  let daysDiff = date2.getDate() - date1.getDate()
  let hours = date2.getHours() - date1.getHours()
  let minutes = date2.getMinutes() - date1.getMinutes()
  let seconds = date2.getSeconds() - date1.getSeconds()

  // Adjust for negative values in months and days
  if (seconds < 0) {
    seconds += 60
    minutes--
  }
  if (minutes < 0) {
    minutes += 60
    hours--
  }
  if (hours < 0) {
    hours += 24
    daysDiff--
  }
  if (daysDiff < 0) {
    // Handle different month lengths
    const daysInPreviousMonth = new Date(date2.getFullYear(), date2.getMonth(), 0).getDate()
    daysDiff += daysInPreviousMonth
    months--
  }
  if (months < 0) {
    months += 12
    years--
  }

  if (years > 0) displayDuration += `${years} tahun, `
  if (months > 0) displayDuration += `${months} bulan, `
  if (daysDiff > 0) displayDuration += `${daysDiff} hari, `
  if (hours > 0) displayDuration += `${hours} jam, `
  if (minutes > 0) displayDuration += `${minutes} menit, `
  if (seconds > 0) displayDuration += `${seconds} detik`

  // Menghapus koma dan spasi tambahan di akhir string
  if (displayDuration.endsWith(', ')) displayDuration = displayDuration.slice(0, -2)

  return displayDuration
}

export const getDateSummary = (days: number): string => {
  const differenceInYears = Math.floor(days / 365)
  const remainingDays = days % 365
  const differenceInMonths = Math.floor(remainingDays / 30)
  const remainingDaysInMonth = remainingDays % 30

  let displayDifference = ''

  if (differenceInYears > 0) {
    return '> 1 Tahun'
  }

  if (differenceInMonths > 0) {
    displayDifference += `${differenceInMonths} Bulan `
  }

  if (remainingDaysInMonth > 0) {
    displayDifference += `${remainingDaysInMonth} Hari`
  }

  if (displayDifference === '') {
    displayDifference = '0 Hari'
  }

  return displayDifference
}

export const getDurationDay = (date: string) => {
  const data = new Date(date)
  const currentDate = new Date()
  data.setHours(0)
  data.setMinutes(0)
  data.setSeconds(0)
  currentDate.setHours(0)
  currentDate.setMinutes(0)
  currentDate.setSeconds(0)

  // Ubah kedua tanggal tersebut menjadi milidetik sejak epoch (1 Januari 1970)
  const dateInMilliseconds = data.getTime()
  const currentDateInMilliseconds = currentDate.getTime()

  // Hitung selisih waktu dalam milidetik
  const timeDifferenceInMilliseconds = currentDateInMilliseconds - dateInMilliseconds

  // Konversi selisih waktu dari milidetik ke hari
  const timeDifferenceInDays = Math.floor(timeDifferenceInMilliseconds / (1000 * 60 * 60 * 24))

  if (timeDifferenceInDays === 0) return 'Hari ini'
  else if (timeDifferenceInDays === 1) return 'Kemarin'
  else if (timeDifferenceInDays < 0) return '-'
  else return timeDifferenceInDays + ' Hari lalu'
}

// export const getProgressPhrase = (progressBar: number) => {
//   if (progressBar <= 25) {
//     return 'Target Masih Jauh, Terus Berjuang!';
//   } else if (progressBar <= 50) {
//     return 'Sudah Setengah Perjalanan, Tetap Semangat!';
//   } else if (progressBar <= 75 || progressBar <= 99) {
//     return 'Hampir Mencapai Target, Sedikit Lagi!';
//   } else if (progressBar >= 100) {
//     return 'Telah Mencapai Target, Kerja Bagus!';
//   }
// };

export const getProgressColor = (progressBar: number) => {
  if (progressBar <= 25) {
    return 'bg-danger'
  } else if (progressBar <= 50) {
    return 'bg-warning'
  } else if (progressBar <= 75 || progressBar <= 99) {
    return 'bg-info'
  } else if (progressBar === 100) {
    return 'bg-primary'
  }
}

export const formatCountdown = (countDown: number) => {
  const minutes = Math.floor(countDown / 60)
  const seconds = countDown % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

export const fetchBlob = async (imageUrl: string): Promise<{ blob: Blob; fileName: string }> => {
  const response = await fetch(imageUrl)
  const blob = await response.blob()

  // Generate a random filename using the current timestamp
  const timestamp = new Date().getTime()
  const randomString = Math.random().toString(36).substring(2, 8) // Generate a random string

  // the extension of the file according to type either image, video or file
  if (blob.type.includes('image')) {
    const extension = blob.type.split('/')[1]
    const fileName = `image_${timestamp}_${randomString}.${extension}`
    return { blob, fileName }
  } else if (blob.type.includes('video')) {
    const extension = blob.type.split('/')[1]
    const fileName = `video_${timestamp}_${randomString}.${extension}`
    return { blob, fileName }
  }
  return { blob, fileName: '' }
}

export const isUrl = (text: string): boolean => {
  // Updated URL regex for non-protocol URLs with subdomain.domain.extension and domain.extension patterns
  const urlRegex =
    /(https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z]{2,}(\.[a-zA-Z]{2,})(\.[a-zA-Z]{2,})?\/[a-zA-Z0-9]{2,}|((https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z]{2,}(\.[a-zA-Z]{2,})(\.[a-zA-Z]{2,})?)|(https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z0-9]{2,}\.[a-zA-Z0-9]{2,}\.[a-zA-Z0-9]{2,}(\.[a-zA-Z0-9]{2,})? /
  return urlRegex.test(text)
}

export const isValidUrl = (url: string) => {
  // Membuat pola regex dengan menyertakan TLD yang valid
  const urlRegex = /^(https:\/\/|http:\/\/)[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}(\/.*)?$/
  return urlRegex.test(url)
}

export const escapeHtml = (text: string) => {
  return text
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

export const formatText = (input: string, participants: ListChatGroupParticipants, groupParticipants: ChatGroupRoom | ChatRoom, wrap: boolean, urlChecking: boolean = false, line?: number) => {
  let output = escapeHtml(input); // escape awal agar aman

  // Format markdown-like: *bold*, _italic_, ~strikethrough~
  output = output.replace(/__(.*?)__/g, '<u>$1</u>');
  output = output.replace(/\*(.*?)\*/g, '<b>$1</b>');
  output = output.replace(/_(.*?)_/g, '<i>$1</i>');
  output = output.replace(/~(.*?)~/g, '<s>$1</s>');

  // Highlight mention @something
  output = output.replace(/@(\w+)/g, (match, username) => {
    const participant = participants[username] ? participants[username] : participants[username+"@lid"];
    const groupParticipant =  (groupParticipants as ChatGroupRoom)?.participants?.find((p) =>  p.phone.includes(username) || p.lid?.includes(username));

    if (participant) {
      return `<span data-id="${participant.phone}" style="color:#21c063">@${participant.name}</span>`;
    } else if (groupParticipant) {
      return `<span data-id="${groupParticipant.phone}" style="color:#21c063">@${groupParticipant.name}</span>`;
    }

    // kalau tidak ditemukan di participants, tetap tampilkan mention biasa
    return `@${username}`;
  });

  // Replace newline with <br>
  output = output.replace(/\n/g, '<br>');

  if (urlChecking) {
    const words = output.split(/(<br\s*\/?>|\s)/).filter(Boolean);
    for (let i = 0; i < words.length; i++) {
      const word = words[i];

      if (word.match(/^<br\s*\/?>$/) || ['(', ')', '[', ']'].includes(word)) continue;

      const cleanWord = word.replace(/<[^>]*>/g, ''); // strip HTML for URL/phone check

      if (isUrl(cleanWord)) {
        const href = cleanWord.startsWith('http') ? cleanWord : `https://${cleanWord}`;
        words[i] = word.replace(
          cleanWord,
          `<a href="${href}" target="_blank" rel="noopener noreferrer">${cleanWord}</a>`
        );
      } else if (isPhoneNumber(cleanWord)) {
        words[i] = word.replace(
          cleanWord,
          `<a href="#">${cleanWord}</a>`
        );
      }
    }
    output = words.join('');
  }

  if (line && line > 0) {
    const lines = output.split('<br>')
    if (!wrap) {
      if (lines.length > line) {
        return lines.slice(0, line).join('<br>') + '...'
      }
      return lines.join('<br>')
    } else {
      return lines.join(' ')
    }
  }

  return output
}

export const generateTextColor = (input: string): string => {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = input.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Hue diputar berdasarkan hash
  const hue = Math.abs(hash) % 360;

  // Saturasi & Lightness di-set agak gelap
  return `hsl(${hue}, 70%, 30%)`; 
}

export const getFilterScopePhrase = (scope: string, title: string, user?: string) => {
  var prefix: string
  var suffix: string

  if (scope.toUpperCase() === 'ALL') {
    prefix = `semua ${title}`
  } else if (user !== undefined) {
    prefix = `${title} milik ${user}`
  } else if (title === 'Target' && scope.toUpperCase() !== 'SELF') {
    prefix = `${title} milik`
  } else {
    prefix = `${title} milik anda`
  }

  switch (scope.toUpperCase()) {
    case 'TEAM':
      suffix = ' dan tim anda'
      break
    case 'WHOLE-TEAM':
      suffix = ' dan seluruh tim di bawah anda'
      break
    default:
      suffix = ''
      break
  }

  if (title === 'Target' && user === undefined) suffix = suffix.replace(' dan', '')
  return `${prefix} ${suffix}`
}

export const handleCaps = (event: any) => {
  var caps = JSON.parse(localStorage.getItem('settings-store')!).state.settings
    .caps_lock_inputs as boolean
  if (caps) {
    event.target.value = event.target.value.toUpperCase()
  }
}

export const handleCapsSelect = (value: string) => {
  var caps = JSON.parse(localStorage.getItem('settings-store')!).state.settings
    .caps_lock_inputs as boolean
  if (caps) {
    return (value = value.toUpperCase())
  }
}

export const randomColor = () => {
  const red = Math.floor(Math.random() * 256)
  const green = Math.floor(Math.random() * 256)
  const blue = Math.floor(Math.random() * 256)

  // Convert the values to hexadecimal format
  const redHex = red.toString(16).padStart(2, '0')
  const greenHex = green.toString(16).padStart(2, '0')
  const blueHex = blue.toString(16).padStart(2, '0')

  // Concatenate the values to form the color code
  const colorCode = `#${redHex}${greenHex}${blueHex}`
  return colorCode
}

export function customDistanceFormat(valueString: number) {
  if (valueString >= 1000) {
    return Math.round(valueString / 1000.0) + ' km'
  } else if (valueString >= 100) {
    return Math.round(valueString) + ' m'
  } else {
    return valueString.toFixed(1) + ' m'
  }
}

export function isColorDarkFromHex(hexColor: string): boolean {
  const luminance: number =
    0.299 * parseInt(hexColor.slice(1, 3), 16) +
    0.587 * parseInt(hexColor.slice(3, 5), 16) +
    0.114 * parseInt(hexColor.slice(5, 7), 16)
  return luminance < 128
}

export const calculateHaversineDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  function toRadians(degrees: number): number {
    return degrees * (Math.PI / 180)
  }

  const R = 6371000 // Earth's radius in meters

  const dLat = toRadians(lat2 - lat1)
  const dLon = toRadians(lon2 - lon1)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  const distance = R * c

  return distance
}

export const customSort = (rows: any, selector: any, direction: any) => {
  return rows.sort((rowA: any, rowB: any) => {
    const aField = selector(rowA)
    const bField = selector(rowB)

    let comparison = 0

    if (aField > bField) {
      comparison = 1
    } else if (aField < bField) {
      comparison = -1
    }

    return direction === 'desc' ? comparison * -1 : comparison
  })
}

export const isUUID = (str: string) => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  return uuidRegex.test(str)
}

export const isImageUrl = (url: string) => {
  if (!url) {
    // Jika URL kosong atau undefined, kembalikan false
    return false
  }

  // Daftar protokol yang valid
  const validProtocols = ['http://', 'https://']

  // Daftar ekstensi file gambar yang valid
  const imageExtensions = ['.jpg', '.jpeg', '.png']

  // Memeriksa protokol yang valid
  const protocolRegex = /^(https?|ftp):\/\//
  const protocolMatch = url.match(protocolRegex)
  if (!protocolMatch) {
    return false
  }
  const protocol = protocolMatch[0]

  // Memeriksa ekstensi file di URL
  const pathnameRegex = /\/[^/?#]+(\.[^/?#]+)($|[?#])/
  const pathnameMatch = url.match(pathnameRegex)
  if (!pathnameMatch) {
    // Jika tidak ada ekstensi dalam path name, maka bukan URL gambar
    return false
  }
  const extension = pathnameMatch[1].toLowerCase() // Mendapatkan ekstensi dalam huruf kecil untuk memastikan kecocokan
  return validProtocols.includes(protocol) && imageExtensions.includes(extension)
}

export const isFileUrl = (url: string) => {
  if (!url) {
    // Jika URL kosong atau undefined, kembalikan false
    return false
  }

  // Daftar protokol yang valid
  const validProtocols = ['http://', 'https://']

  // Daftar ekstensi file gambar yang valid
  const imageExtensions = ['.pdf', '.xls', '.xlsx', '.doc', '.docx', '.ppt', '.pptx', '.mp4', '.mkv', '.webm', '.mov']

  // Memeriksa protokol yang valid
  const protocolRegex = /^(https?|ftp):\/\//
  const protocolMatch = url?.match(protocolRegex)
  if (!protocolMatch) {
    return false
  }
  const protocol = protocolMatch[0]

  // Memeriksa ekstensi file di URL
  const pathnameRegex = /\/[^/?#]+(\.[^/?#]+)($|[?#])/
  const pathnameMatch = url?.match(pathnameRegex)
  if (!pathnameMatch) {
    // Jika tidak ada ekstensi dalam path name, maka bukan URL gambar
    return false
  }
  const extension = pathnameMatch[1].toLowerCase() // Mendapatkan ekstensi dalam huruf kecil untuk memastikan kecocokan
  return validProtocols.includes(protocol) && imageExtensions.includes(extension)
}

export const sortColumnDatatable = (arr: any, fromIndex: number, toIndex: number) => {
  const item = arr.splice(fromIndex, 1)[0]
  arr.splice(toIndex, 0, item)
}

export const getFileType = (url: string) => {
  // Ekstrak ekstensi file dari URL
  const fileExtension = url.split('.').pop()?.toLowerCase()

  // Daftar tipe file dan ekstensinya
  const fileTypes = {
    ppt: ['ppt', 'pptx'],
    pdf: ['pdf'],
    doc: ['doc', 'docx'],
    xlsx: ['xlsx', 'xls'],
    mp4: ['mp4', 'mkv', 'webm', 'mov'],
  }

  // Cari jenis file berdasarkan ekstensi
  for (const [type, extensions] of Object.entries(fileTypes)) {
    if (extensions.includes(fileExtension!)) {
      return type
    }
  }

  // Jika ekstensi tidak ditemukan, kembalikan 'unknown'
  return 'unknown'
}
export const truncateText = (text: string, textLimit: number) => {
  if (text && text.length > textLimit) {
    return text.slice(0, textLimit) + '...'
  }
  return text
}

export const hexWithOpacity = (hex: string, opacity: number) => {
  hex = hex.replace('#', '')
  if (hex.length !== 6) {
    throw new Error('Hex color code must be 6 characters long.')
  }

  // Memastikan opacity dalam rentang yang benar
  if (opacity < 0 || opacity > 100) {
    throw new Error('Opacity must be a value between 0 and 100.')
  }

  // Mengonversi opacity dari persentase ke nilai hex (00 hingga FF)
  let alpha = Math.round((opacity / 100) * 255)
    .toString(16)
    .padStart(2, '0')
    .toUpperCase()

  // Menggabungkan hex color dengan nilai alpha
  return `#${hex}${alpha}`
}

export const getNameUrl = (url: string) => {
  const name = url.split('/').pop()
  return name
}

export const isValidTypeAttachment = (fileName: string, validExtensions: string[]) => {
  // Ambil ekstensi file dari fileName
  const fileExtension = fileName?.split('.').pop()?.toLowerCase()

  // Cek apakah fileExtension ada di dalam daftar validExtensions
  return validExtensions.includes(fileExtension!)
}

export const calculateDistanceLocation = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
) => {
  const R = 6371 // Jari-jari bumi dalam kilometer
  const dLat = (lat2 - lat1) * (Math.PI / 180) // Konversi selisih lintang ke radian
  const dLon = (lon2 - lon1) * (Math.PI / 180) // Konversi selisih bujur ke radian

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
    Math.cos(lat2 * (Math.PI / 180)) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  const distance = R * c // Jarak dalam kilometer

  // Menghilangkan pembulatan dengan membatasi 2 digit tanpa pembulatan
  return Math.round(distance * 100) / 100 // Jarak dengan maksimal 2 digit tanpa pembulatan
}

export const convertToMinutes = (data: { day: number; hour: number; minute: number }): number => {
  const minutesFromDays = data.day * 24 * 60 // 1 hari = 24 jam, 1 jam = 60 menit
  const minutesFromHours = data.hour * 60 // 1 jam = 60 menit
  const totalMinutes = minutesFromDays + minutesFromHours + data.minute

  return totalMinutes
}

export const convertFromMinutes = (totalMinutes: number) => {
  const day = Math.floor(totalMinutes / (24 * 60)) // 1 hari = 24 jam * 60 menit
  const remainingMinutesAfterDays = totalMinutes % (24 * 60)

  const hour = Math.floor(remainingMinutesAfterDays / 60) // 1 jam = 60 menit
  const minute = remainingMinutesAfterDays % 60 // sisa menit

  return {
    day: day,
    hour: hour,
    minute: minute,
  }
}

export const toTitleCase = (str: string): string => {
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export const customNumberFormatString = (valueString: string) => {
  const onlyNumbers = valueString.replace(/\D+/g, '') // remove non-digits
  const parts = onlyNumbers.split(',')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return parts.join(',')
}

export const isBeforeToday = (date: Date) => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return date < today
}

export const toRupiah = (amount: number) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(amount)
}
export const toUSD = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(amount)
}

export const dataURLtoFile = (dataUrl: string, filename: string, Buffer: any): File | undefined => {
  const arr = dataUrl.split(',')
  if (arr.length < 2) {
    return undefined
  }
  const mimeArr = arr[0].match(/:(.*?);/)
  if (!mimeArr || mimeArr.length < 2) {
    return undefined
  }
  const mime = mimeArr[1]
  const buff = Buffer.from(arr[1], 'base64')
  return new File([buff], filename, { type: mime })
}

export const getRandomFileName = () => {
  var timestamp = new Date().toISOString().replace(/[-:.]/g, '')
  var random = ('' + Math.random()).substring(2, 8)
  var random_number = timestamp + random
  return random_number
}

export const isEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export const isImage = (name: string) => /\.(jpg|jpeg|png|gif)$/i.test(name)
export const isVideo = (name: string) => /\.(mp4|mkv|webm|mov)$/i.test(name)
export const isFile = (name: string) => /\.(pdf|xls|xlsx|doc|docx|ppt|pptx|txt|text)$/i.test(name)

export const countTopUserPercent = (value: number, target: number) => {
  if (target) {
    let result = (value / target) * 100

    if (isNaN(result)) {
      result = 0
    } else if (result === Infinity && value >= target) {
      result = 100
    }

    if (value === 0 && value === target) {
      result = 0
    }

    return Math.round(result)
  } else return 0
}

export const getDataById = (id: string | string[], data: any[]) => {
  if (Array.isArray(id)) {
    return data.filter((item: any) => id.includes(item.id))
  } else {
    return data.find((item: any) => item.id === id)
  }
}

export const removeDotNominal = (value: string) => {
  const newValue = value.replace(/\./g, '')
  return Number(newValue)
}

export const getPinningStyles = (
  column: Column<PaymentTermin | TempPaymentTermin>,
  isHeader: boolean = false
): CSSProperties => {
  if (column.id === 'actions') {
    return {
      position: 'sticky',
      left: 0,
      backgroundColor: isHeader ? '#D4D4D4' : 'white',
      zIndex: 2,
      boxShadow: '2px 0 4px -2px rgba(0,0,0,0.2)',
      width: column.getSize(),
    }
  }
  return {}
}

export const isAfterToday = (date: Date) => {
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  tomorrow.setHours(0, 0, 0, 0)

  return date >= tomorrow
}

export const transformDataLimits = (
  dataYearly: YearlyData,
  currency: ICurrency,
  userId: string,
  userName: string
) => {
  let transformedData: ITransformedData = {}
  for (const year in dataYearly) {
    const yearData = dataYearly[year]
    transformedData[`year_${year}`] = {
      userName: userName,
      amount: yearData.limitYear,
      currency: currency,
      id: yearData.idLimitYear,
      isSet: yearData.isSet,
      period: yearData.period,
      userId: userId,
      type: 'YEARLY',
    }
    for (const month in yearData.dataMonthly) {
      const monthData = yearData.dataMonthly[month]
      transformedData[`month_${month}_${year}`] = {
        userId: userId,
        userName: userName,
        amount: monthData.limitMonth,
        currency: currency,
        id: monthData.idLimitMonth,
        isSet: monthData.isSet,
        period: monthData.period,
        type: 'MONTHLY',
      }
    }
  }
  return transformedData
}

export const normalizeDate = (dateString: string): string => {
  if (!dateString) return ''

  const parts = dateString.split('-')
  if (parts.length !== 3) return dateString

  const [year, month, day] = parts

  const normalizedMonth = month.padStart(2, '0')
  const normalizedDay = day.padStart(2, '0')

  return `${year}-${normalizedMonth}-${normalizedDay}`
}

export function roundNominal(data: string | number) {
  if (typeof data === 'string') {
    let checkNominal = data.includes('Rp')
    if (checkNominal) {
      const numericString = data.replace(/Rp|\./g, '').replace(',', '.')
      const numeric = parseFloat(numericString)
      const roundedUp = Math.ceil(numeric)
      return 'Rp ' + customNumberFormat(roundedUp)
    } else {
      return data
    }
  } else {
    return Math.ceil(data)
  }
}

export const convertHtmlToWhatsappFormat = (html: string | string[]): string => {
  if (!html || html.length === 0 || html === '<p><br></p>') return '';

  const extractEmojis = (input: string): string =>
    input.replace(/<span[^>]*ql-emojiblot[^>]*>.*?<span[^>]*ap-[^>]*>(.*?)<\/span>.*?<\/span>/g, '$1').trim();

  const decodeHtmlEntities = (str: string): string => {
    return str
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ');
  };

  const fixHtmlSpacing = (html: string): string => {
    return html
      .replace(/(\s+)<\/s><\/em>/g, '</s></em>$1')
      .replace(/<em><s>(\s+)/g, '$1<em><s>')
      .replace(/(\s+)<\/s><\/strong>/g, '</s></strong>$1')
      .replace(/<strong><s>(\s+)/g, '$1<strong><s>')
      .replace(/(\s+)<\/em>/g, '</em>$1')
      .replace(/<em>(\s+)/g, '$1<em>')
      .replace(/(\s+)<\/strong>/g, '</strong>$1')
      .replace(/<strong>(\s+)/g, '$1<strong>')
      .replace(/(\s+)<\/s>/g, '</s>$1')
      .replace(/<s>(\s+)/g, '$1<s>');
  };

  const replaceHtmlTags = (input: string): string => {
    return input
      .replace(/<strong>(.*?)<\/strong>/g, '*$1*')
      .replace(/<b>(.*?)<\/b>/g, '*$1*')
      .replace(/<em>(.*?)<\/em>/g, '_$1_')
      .replace(/<i>(.*?)<\/i>/g, '_$1_')
      .replace(/<s>(.*?)<\/s>/g, '~$1~')
      .replace(/<u>(.*?)<\/u>/g, '__$1__')
      .replace(/<br\s*\/?>/g, '\n')
      .replace(/<p>\s*<\/p>/g, '\n')
      .replace(/<p>(.*?)<\/p>/gs, '$1\n')
      .replace(/<span[^>]*>.*?<\/span>/g, '')
      .replace(/<\/?span>/g, '')
      .replace(/\u200B|\uFEFF/g, '')
      .trim();
  };

  let processed = Array.isArray(html) ? html.join('') : html;
  processed = fixHtmlSpacing(processed);
  processed = extractEmojis(processed);
  processed = decodeHtmlEntities(processed); // ⬅️ decode sebelum replace
  processed = replaceHtmlTags(processed);

  return processed;
};

export const convertWhatsappToHtml = (
  message: (string | Record<string, any>)[],
  returnKey = 'defaultValue',
  isHtml: boolean = false
): string => {
  if (!message || message.length === 0) return '';

  const parsedMessage = message
    .map((part) => {
      if (typeof part === 'string') return part;

      if (typeof part === 'object') {
        for (const key in part) {
          if (key === 'timestamp') {
            return moment(part[key].defaultValue).format(part[key]?.format)
          }
          if (returnKey === 'defaultValue' && part[key]?.defaultValue) {
            return part[key].defaultValue;
          }
          if (returnKey === 'source' && part[key]?.source) {
            return `@[${part[key].id}∋${part[key].source}]`;
          }
        }
        return ''
      }

      return '';
    })
    .join('')

  if (!isHtml) return parsedMessage;

  return parsedMessage
    .replace(/__(.*?)__/g, '<u>$1</u>')
    .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
    .replace(/_(.*?)_/g, '<em>$1</em>')
    .replace(/~(.*?)~/g, '<s>$1</s>')
    .replace(/\n/g, '<br>')
  // .replace(/ {2,}/g, (m) => '&nbsp;'.repeat(m.length)); // multiple spaces → &nbsp;
};



export function convertWhatsappToHtmlDiv(message?: string): string {
  if (!message) return '' // Jika message undefined, null, atau kosong, kembalikan string kosong

  return message
    .replace(/\*(.*?)\*/g, '<strong>$1</strong>') // Bold (*text*)
    .replace(/_(.*?)_/g, '<em>$1</em>') // Italic (_text_)
    .replace(/~(.*?)~/g, '<s>$1</s>') // Strikethrough (~text~)
    .replace(/```(.*?)```/gs, '<pre>$1</pre>') // Code Block (```text```)
    .replace(/__(.*?)__/g, '<u>$1</u>') // Underline (__text__)
    .replace(/\n/g, '<br>') // Mengubah newline menjadi <br>
}

export const inputNumericFormat = (e: React.KeyboardEvent<HTMLInputElement>): boolean => {
  const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab']
  const isCtrlCombo = e.ctrlKey && ['a', 'c', 'v'].includes(e.key.toLowerCase())

  if (!allowedKeys.includes(e.key) && !/^[0-9]$/.test(e.key) && !isCtrlCombo) {
    return false
  }
  return true
}

export const convertImageUrlToFile = async (imageUrl: string, fileName: string) => {
  const response = await fetch(imageUrl)
  const blob = await response.blob()
  return new File([blob], fileName, { type: blob.type })
}

export function formatTimeByFrame(time: any, timeFrame: string): string {
  switch (timeFrame) {
    case 'DAILY':
      return moment(time).format('DD MMMM YYYY') // Contoh: "01 January 2024"
    case 'MONTHLY':
      return moment(time).format('MMMM YYYY') // Contoh: "January 2024"
    case 'WEEKLY': {
      const startDate = moment(time) // Tanggal awal
      const endDate = moment(time).add(6, 'days') // Tanggal akhir (6 hari setelah tanggal awal)
      return `${startDate.format('DD MMMM YYYY')} - ${endDate.format('DD MMMM YYYY')}` // Contoh: "01 January 2024 - 07 January 2024"
    }
    default:
      return time
  }
}

export const renderWithFlexRender = (cell: any) => {
  return flexRender(cell.column.columnDef.cell, cell.getContext())
}

export const renderWithouFlexRender = (cell: any) => {
  return typeof cell.column.columnDef.cell === 'function'
    ? cell.column.columnDef.cell(cell.getContext())
    : cell.column.columnDef.cell
}

export const customNumberFormatNew = (number: number | string | undefined | null): string => {
  if (number === undefined || number === null) return '0'

  const num = typeof number === 'string' ? parseFloat(number) : number
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}

export function currencyFormatter(
  valueNumber: string | number,
  companyCurrencyNeedRound: boolean = false, // decimal config
  currencyType?: CurrencyCodes,
  config?: {
    isInputFormat?: boolean
    showPrefix?: boolean
    isRoundDecimal?: boolean // decimal config
    roundType?: 'round' | 'ceil' | 'floor'
    integerPartDigits?: number
    decimalPartDigits?: number // decimal config
    defaultZero?: boolean
    showZeroDecimal?: boolean // decimal config
  }
): string {
  if (valueNumber === '' || valueNumber === null || valueNumber === undefined)
    return config?.defaultZero ? '0' : ''

  // valueNumber = valueNumber.toString().includes(',') ? valueNumber + '.' : valueNumber;
  let cleanString = valueNumber.toString().replace(/[^0-9.]/g, '') // Hapus semua kecuali angka & titik

  const currencyFormatterValidation = currencyFormatterPreventPropagation(cleanString, {
    decimalPartDigits: config?.decimalPartDigits ?? 2,
  })

  // **Mencegah titik di awal input**
  if (cleanString.startsWith(".")) {
    cleanString = ""; // Ubah menjadi ""
  }

  // Pastikan hanya ada satu titik desimal
  const firstDotIndex = cleanString.indexOf('.')
  if (firstDotIndex !== -1) {
    cleanString =
      cleanString.slice(0, firstDotIndex + 1) +
      cleanString.slice(firstDotIndex + 1).replace(/\./g, '')
  }

  // **Bulatkan nilai desimal, default = true**
  if (config?.isRoundDecimal === undefined || config?.isRoundDecimal === true) {
    if (
      !currencyFormatterValidation.hasTrailingDot &&
      !currencyFormatterValidation.hasTrailingDotZero
    ) {
      cleanString = roundDecimalToTwo(Number(cleanString)).toString()
    }
  }

  let hasDecimal = cleanString.includes('.')
  let [integerPart, decimalPart = ''] = cleanString.split('.')

  // **Menghapus leading zero yang tidak valid**
  if (!hasDecimal) {
    integerPart = integerPart.replace(/^0+(?=\d)/, '') // Hapus nol di awal kecuali kalau cuma nol
  }

  // **Batasi panjang integerPart sesuai integerPartDigits**
  integerPart = integerPart.slice(0, config?.integerPartDigits ?? 15)

  let numValue = Number(cleanString)

  if (companyCurrencyNeedRound) {
    // Gunakan metode pembulatan yang dipilih
    if (config?.roundType === 'ceil') {
      numValue = Math.ceil(numValue)
    } else if (config?.roundType === 'floor') {
      numValue = Math.floor(numValue)
    } else {
      numValue = Math.round(numValue)
    }

    integerPart = numValue.toString()
    decimalPart = '' // Hapus desimal setelah dibulatkan

    // **🔹 Batasi kembali integerPart ke 16 digit setelah pembulatan**
    integerPart = integerPart.slice(0, config?.integerPartDigits ?? 16)
  } else {
    // Batasi desimal sesuai `decimalPartDigits`
    decimalPart = decimalPart.slice(0, config?.decimalPartDigits ?? 2)
  }

  // Format angka dengan pemisah ribuan
  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  // **Mengembalikan nilai desimal dengan zero suffix **
  let getDecimalPartDigits = config?.decimalPartDigits ?? 2
  if (
    !companyCurrencyNeedRound &&
    config?.showZeroDecimal &&
    decimalPart.length < getDecimalPartDigits
  ) {
    decimalPart = decimalPart + '0'.repeat(getDecimalPartDigits - decimalPart.length)
  }

  let formattedValue =
    hasDecimal && !companyCurrencyNeedRound ? `${integerPart}.` + decimalPart : integerPart

  return config?.showPrefix
    ? `${getCurrencySymbol(currencyType ?? 'USD')} ${formattedValue}`
    : formattedValue
}


export const getCurrencySymbol = (currencyType: CurrencyCodes): string => {
  return currencylocales.find((item) => item.currencyCode === currencyType)?.currencySymbol ?? "❓";
}

// Fungsi untuk parsing kembali ke angka
export function parseFormattedCurrency(value: string): string {
  // Hapus semua karakter kecuali angka, titik, dan koma
  const cleanedValue = value.replace(/[^\d.,]/g, '')

  // Jika formatnya pakai koma sebagai desimal (Eropa)
  if (cleanedValue.includes(',') && cleanedValue.lastIndexOf(',') > cleanedValue.lastIndexOf('.')) {
    return cleanedValue
      .replace(/\./g, '') // Hapus semua titik ribuan
      .replace(',', '.') // Ubah koma desimal jadi titik
  }

  // Jika formatnya pakai titik sebagai desimal (Amerika)
  return cleanedValue.replace(/,/g, '') // Hapus semua koma ribuan
}

export function currencyFormatterPreventPropagation(
  value: string | number,
  config?: {
    decimalPartDigits: number
  }
): {
  hasTrailingDot: boolean
  hasTrailingMultipleDot: boolean
  hasTrailingMultipleComma: boolean
  hasTrailingDotZero: boolean
  hasTrailingComma: boolean
} {
  if (typeof value === 'number') {
    value = value.toString()
    value = currencyFormatter(value, false)
  }
  const hasTrailingDot = /\.$/.test(value)
  // const hasTrailingDotZero = /\.\d*0$/.test(value);
  const hasTrailingMultipleDot = (value.match(/\./g) || []).length > 1
  const hasTrailingComma = /\,$/.test(value)
  const hasTrailingMultipleComma = (value.match(/,/g) || []).length > 1

  // Cek apakah ada titik desimal di value
  const decimalMatch = value.match(/\.(\d*)$/)
  let hasTrailingDotZero = false

  if (decimalMatch) {
    const decimalPart = decimalMatch[1] // Ambil angka setelah titik
    const decimalPartDigits = config?.decimalPartDigits ?? 2

    // Jika decimalPart hanya nol dan panjangnya ≤ batas, maka true
    const isOnlyZeroWithinLimit =
      decimalPart.length <= decimalPartDigits && /^[0]+$/.test(decimalPart)

    hasTrailingDotZero = isOnlyZeroWithinLimit
  }

  // console.log('value', value, 'hasTrailingDot', hasTrailingDot, 'hasTrailingDotZero', hasTrailingDotZero, 'hasTrailingMultipleDot', hasTrailingMultipleDot, 'hasTrailingComma', hasTrailingComma)
  return {
    hasTrailingDot,
    hasTrailingDotZero,
    hasTrailingMultipleDot,
    hasTrailingComma,
    hasTrailingMultipleComma,
  }
}

export function roundDecimalToTwo(num: number, roundType?: 'round' | 'ceil' | 'floor'): number {
  return Math.round(num * 100) / 100
}

export function unFormatNumber(num: string): string {
  return num.replaceAll(',', '')
}

export const formattedCurrency = (
  locale: string,
  currency: string,
  amount: string | number,
  minimumFractionDigits: number = 0
) => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    currencyDisplay: 'code',
    minimumFractionDigits: minimumFractionDigits, // Menghilangkan desimal
  }).format(typeof amount == 'string' ? Number(amount) : amount)
}

export const base64ToFile = (base64: string, fileName: string): File => {
  const byteString = atob(base64.split(',')[1]) // Decode Base64
  const arrayBuffer = new ArrayBuffer(byteString.length)
  const uint8Array = new Uint8Array(arrayBuffer)

  for (let i = 0; i < byteString.length; i++) {
    uint8Array[i] = byteString.charCodeAt(i)
  }

  const mimeType = base64.match(/data:(.*?);base64/)?.[1] || 'application/octet-stream' // Extract MIME type
  const blob = new Blob([uint8Array], { type: mimeType })
  return new File([blob], fileName, { type: mimeType })
}

export const convertNumberToText = (number: number): string => {
  const angka = [
    '',
    'satu',
    'dua',
    'tiga',
    'empat',
    'lima',
    'enam',
    'tujuh',
    'delapan',
    'sembilan',
    'sepuluh',
    'sebelas',
  ]

  switch (true) {
    case number < 12:
      return angka[number]
    case number < 20:
      return angka[number % 10] + ' belas'
    case number < 100:
      return angka[Math.floor(number / 10)] + ' puluh ' + angka[number % 10]
    case number < 200:
      return 'seratus ' + convertNumberToText(number % 100)
    case number < 1000:
      return angka[Math.floor(number / 100)] + ' ratus ' + convertNumberToText(number % 100)
    case number < 2000:
      return 'seribu ' + convertNumberToText(number % 1000)
    case number < 10 ** 6:
      return (
        convertNumberToText(Math.floor(number / 1000)) +
        ' ribu ' +
        convertNumberToText(number % 1000)
      )
    case number < 10 ** 9:
      return (
        convertNumberToText(Math.floor(number / 1000000)) +
        ' juta ' +
        convertNumberToText(number % 1000000)
      )
    case number < 10 ** 12:
      return (
        convertNumberToText(Math.floor(number / 1000000000)) +
        ' miliar ' +
        convertNumberToText(number % 1000000000)
      )
    case number < 10 ** 15:
      return (
        convertNumberToText(Math.floor(number / 1000000000000)) +
        ' triliun ' +
        convertNumberToText(number % 1000000000000)
      )
    case number < 10 ** 18:
      return (
        convertNumberToText(Math.floor(number / 1000000000000000)) +
        ' kuadriliun ' +
        convertNumberToText(number % 1000000000000000)
      )
    case number < 10 ** 21:
      return (
        convertNumberToText(Math.floor(number / 1000000000000000000)) +
        ' kuantiliun ' +
        convertNumberToText(number % 1000000000000000000)
      )
    default:
      return 'not defined'
  }
}

export const convertToUTC = (
  inputTime: string,
  userTimezone: string,
  isPlus: boolean = true
): string => {
  // Buat Date dari input UTC
  const date = new Date(inputTime)

  // Ambil offset dari timezone target
  const timeZonePart = new Intl.DateTimeFormat('en-US', {
    timeZone: userTimezone,
    hour12: false,
    hour: '2-digit',
    timeZoneName: 'shortOffset',
  })
    .formatToParts(date)
    .find((part) => part.type === 'timeZoneName')

  // Ekstrak angka offset dengan aman
  const timeZoneOffset = timeZonePart?.value.match(/[-+]\d+/)?.[0] || '+0'

  // Ubah offset ke milidetik
  const offsetMillis = parseInt(timeZoneOffset) * 60 * 60 * 1000

  // Tentukan apakah offset ditambahkan atau dikurangi
  const adjustedTime = isPlus
    ? new Date(date.getTime() + offsetMillis)
    : new Date(date.getTime() - offsetMillis)

  return adjustedTime.toISOString()
}

export const formatBytes = (bytes: number) => {
  if (bytes === 0) return '0 Bytes'

  const units = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const k = 1024
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  const size = bytes / Math.pow(k, i)

  // Hapus trailing .00 kalau tidak dibutuhkan
  const formattedSize = Number(size.toFixed(2)).toString()

  return `${formattedSize} ${units[i]}`
}

export const customNumberFormatReminderMeeting = (valueString: string) => {
  const onlyNumbers = valueString.replace(/\D+/g, '');  // remove non-digits
  const parts = onlyNumbers.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return parts.join('.');
}

export const getLongitudeLatitude = (location: string) => {
  try {
    const nameMatch = location.match(/place\/(.*?)\/(@|$)/);
    const locationName = nameMatch ? decodeURIComponent(nameMatch[1].replace(/\+/g, ' ')) : '';

    const coordsMatch = location.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);

    if (!coordsMatch) {
      return { latitude: null, longitude: null, locationName };
    }

    const latitude = parseFloat(coordsMatch[1]);
    const longitude = parseFloat(coordsMatch[2]);

    return {
      latitude,
      longitude,
      locationName
    };
  } catch (error) {
    console.error('Error parsing Google Maps URL:', error);
    return {
      latitude: null,
      longitude: null,
      locationName: ''
    };
  }
};

export const compressImage = (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = (event) => {
      const img = new Image()
      img.src = event.target?.result as string
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions while maintaining aspect ratio
        const maxWidth = 1200;
        const maxHeight = 1200;

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width)
            width = maxWidth
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height)
            height = maxHeight
          }
        }

        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d')
        ctx?.drawImage(img, 0, 0, width, height)

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              })
              resolve(compressedFile)
            } else {
              reject(new Error('Canvas to Blob conversion failed'))
            }
          },
          'image/jpeg',
          0.7  // Compression quality (0.7 = 70% quality)
        );
      };
    };
    reader.onerror = (error) => reject(error);
  });
};

export function elipsisChar(text: string, length: number) {
  if (text.length > length) {
    return text.slice(0, length) + '...';
  } else {
    return text;
  }
}

export const getColorByProgress = (progress: number) => {
  if (progress > 0 && progress <= 70) {
    return `#65AF8D`
  } else if (progress > 70 && progress <= 90) {
    return `#FFD559`
  } else if (progress > 90 && progress <= 100) {
    return `#E77883`
  } else {
    return `#65AF8D`
  }
}

export const getSubString = (id: string) => {
  const splitData = id?.split('-');
  return splitData?.[splitData.length - 1].substring(7, splitData?.[splitData.length - 1].length);
}

export const checkUsername = (username: string) => {
  const regex = /^[a-zA-Z0-9._-]+$/;
  return regex.test(username);
}

export const isInvalidDotInput = (value: string | null): boolean => {
  if (value === null) return false;

  const trimmed = value.toString().trim();
  return trimmed !== '' && trimmed.endsWith('.');
};

export const fixFloatingPoint = (num: number, selectMultiPipeline: SettingMultiPipelines): number => {
  const decimals = selectMultiPipeline.currencyMinorUnit
  return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
};

export const sanitizeTagHtml = (html: string) => {
  return html.replace(/<[^>]*>?/g, '');
}

export const checkCommonPatterns = (password: string) => {
  const lowerPassword = password.toLowerCase();

  // Common weak patterns
  const commonPatterns = [
    'password', 'password123', '123456', '1234567', '12345678', '123456789', '1234567890',
    'qwerty', 'qwertyui', 'qwertyuiop', 'asdfgh', 'asdfghjk', 'asdfghjkl',
    'zxcvbn', 'zxcvbnm', 'abcdef', 'abcdefg', 'abcdefgh', 'abcdefghi',
    'admin', 'administrator', 'user', 'login', 'welcome', 'master'
  ];

  // Check for common patterns
  for (const pattern of commonPatterns) {
    if (lowerPassword.includes(pattern)) {
      return false;
    }
  }

  // Check for sequential numbers (e.g., 12345, 23456, 98765)
  for (let i = 0; i <= password.length - 4; i++) {
    const substr = password.substring(i, i + 4);
    if (/^\d{4}$/.test(substr)) {
      const digits = substr.split('').map(Number);
      let isSequential = true;
      let isReverse = true;

      for (let j = 1; j < digits.length; j++) {
        if (digits[j] !== digits[j - 1] + 1) isSequential = false;
        if (digits[j] !== digits[j - 1] - 1) isReverse = false;
      }

      if (isSequential || isReverse) return false;
    }
  }

  // Check for sequential letters (e.g., abcd, dcba)
  for (let i = 0; i <= lowerPassword.length - 4; i++) {
    const substr = lowerPassword.substring(i, i + 4);
    if (/^[a-z]{4}$/.test(substr)) {
      const chars = substr.split('').map(c => c.charCodeAt(0));
      let isSequential = true;
      let isReverse = true;

      for (let j = 1; j < chars.length; j++) {
        if (chars[j] !== chars[j - 1] + 1) isSequential = false;
        if (chars[j] !== chars[j - 1] - 1) isReverse = false;
      }

      if (isSequential || isReverse) return false;
    }
  }

  // Check for repetitive patterns (e.g., aaaa, 1111, abab)
  for (let i = 0; i <= password.length - 4; i++) {
    const substr = password.substring(i, i + 4);

    // Check for same character repeated
    if (substr[0] === substr[1] && substr[1] === substr[2] && substr[2] === substr[3]) {
      return false;
    }

    // Check for alternating pattern (e.g., abab, 1212)
    if (substr[0] === substr[2] && substr[1] === substr[3] && substr[0] !== substr[1]) {
      return false;
    }
  }

  return true;
};

export const getDiscountType = (type: string, selectMultiPipeline: SettingMultiPipelines) => {
  if (type === "PERCENTAGE") {
    return "%"
  }
  return selectMultiPipeline.currencyCode
}

export const onlyNumberInput = (e: React.FormEvent<HTMLInputElement>, isAllowNegative: boolean = true) => {
  if (isAllowNegative) {
    e.currentTarget.value = e.currentTarget.value
      .replace(/(?!^)-/g, '')        // hapus semua '-' kecuali di awal
      .replace(/[^0-9.,-]/g, '');    // hanya izinkan angka, titik, koma, minus
    return
  }
  e.currentTarget.value = e.currentTarget.value.replace(/[^0-9.,]/g, '')

}

export const changeValueIfStringIntoNumber = (value: string | number) => {
  if (typeof value === 'string') {
    return Number(value.replaceAll(/[,]/g, ''))
  } else {
    return value
  }
}

export const calculateProductTotal = (
  qty: number,
  price: number,
  discount: number,
  discountType: 'NOMINAL' | 'PERCENTAGE',
  vatPercentage: number,
  selectMultiPipeline: SettingMultiPipelines,
  isIncludeVat: boolean,
  originalPrice?: number | string
): string => {
  let discountAmount = 0;
  let totalAfterVAT = 0;

  if (isIncludeVat) {
    if (discountType === 'PERCENTAGE') {
      discountAmount = (discount / 100) * price;
    } else {
      discountAmount = discount;
    }

    const priceAferVat = price + (vatPercentage / 100) * price;
    const priceAfterDiscount = priceAferVat - discountAmount;
    totalAfterVAT = priceAfterDiscount * qty;
  } else {
    const productPrice = originalPrice ?
      (typeof originalPrice === 'string' ? Number(originalPrice.replace(/,/g, '')) : originalPrice) :
      price;

    if (discountType === 'PERCENTAGE') {
      discountAmount = (discount / 100) * productPrice;
    } else {
      discountAmount = discount;
    }

    const total = qty * (productPrice - discountAmount);
    totalAfterVAT = selectMultiPipeline.currencyIsRounded ?
      Math.round(total + Math.round((vatPercentage / 100) * total)) :
      total + ((vatPercentage / 100) * total);
  }

  if (isNaN(totalAfterVAT) || totalAfterVAT < 0) {
    return "";
  } else if (totalAfterVAT === 0) {
    return "0";
  }

  let result = (selectMultiPipeline.currencyCode === 'IDR' && selectMultiPipeline.currencyIsRounded)
    ? Math.round(Number(totalAfterVAT))
    : roundDecimalToTwo(Number(totalAfterVAT));

  return currencyFormatter(result.toString(), selectMultiPipeline.currencyIsRounded);
};

function isFloat(n: number) {
  return Number(n) === n && !Number.isInteger(n);
}

export const revertIncludeVat = (price: number, vatPercentage: number, isIncludeVat: boolean, selectMultiPipeline: SettingMultiPipelines) => {
  const newPrice = isFloat(price) && selectMultiPipeline.currencyCode === 'USD' ? price : Math.trunc(price)
  if (isIncludeVat) {
    if (selectMultiPipeline.currencyCode === 'USD') {
      return (newPrice * (1 + (vatPercentage / 100)))
    } else {
      return Math.trunc(newPrice * (1 + (vatPercentage / 100)))
    }
  }
  return price
}

export const formatDateWithDayName = (date: string) => {
  // console.log(date, 'date') // 2025-07-29
  const dateObj = new Date(date);
  const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
  const getDate = String(dateObj.getDate()).padStart(2, '0');
  const getMonthName = dateObj.toLocaleDateString('en-US', { month: 'long' });
  const getYear = dateObj.getFullYear();

  return `${dayName}, ${getDate} ${getMonthName} ${getYear}`;
}

export const getDateOnly = (noteDate: string) => {
  // noteDate: Monday, 29 July 2025
  const date = noteDate.split(',')[1].trim()
  const dateComponents = date.split(' ')

  const getDate = dateComponents[0]
  const getMonth = dateComponents[1]
  const getYear = dateComponents[2]

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  const monthNumber = (monthNames.indexOf(getMonth) + 1).toString().padStart(2, '0')

  const reverseDate = `${getYear}-${monthNumber}-${getDate}`
  return reverseDate
}

export const isValidDateFormat = (noteDate: string): boolean => {
  // Validate format: "DayName, DD Month YYYY" (e.g., "Tuesday, 05 August 2025")
  if (!noteDate || typeof noteDate !== 'string') {
    return false;
  }

  // Check basic pattern with regex - date only without time
  const datePattern = /^[A-Za-z]+,\s\d{1,2}\s[A-Za-z]+\s\d{4}$/;

  if (!datePattern.test(noteDate.trim())) {
    return false;
  }

  try {
    // Split by comma to get day name and date part
    const parts = noteDate.split(',');
    if (parts.length !== 2) {
      return false;
    }

    const dayName = parts[0].trim();
    const datePart = parts[1].trim();

    // Validate day names
    const validDayNames = [
      'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
    ];
    if (!validDayNames.includes(dayName)) {
      return false;
    }

    // Split date part to get day, month, year (only 3 components expected)
    const dateComponents = datePart.split(' ');
    if (dateComponents.length !== 3) {
      return false;
    }

    const day = dateComponents[0];
    const month = dateComponents[1];
    const year = dateComponents[2];

    // Validate day (01-31)
    const dayNum = parseInt(day, 10);
    if (isNaN(dayNum) || dayNum < 1 || dayNum > 31) {
      return false;
    }

    // Validate month names
    const validMonthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    if (!validMonthNames.includes(month)) {
      return false;
    }

    // Validate year (4 digits)
    const yearNum = parseInt(year, 10);
    if (isNaN(yearNum) || year.length !== 4) {
      return false;
    }

    return true;
  } catch (error) {
    return false;
  }
}

export const customThousandSeparatorDot = (
  inputValue: string | number,
  isAllowNegative: boolean = true
): string => {
  if (typeof inputValue === 'number') {
    const numValue = Number(inputValue);
    if (isNaN(numValue)) return "";

    const isNegative = numValue < 0 && isAllowNegative;
    const [integerPart, decimalPart = ''] = Math.abs(numValue).toString().split('.');
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    inputValue = decimalPart
      ? `${isNegative ? '-' : ''}${formattedInteger},${decimalPart}`
      : `${isNegative ? '-' : ''}${formattedInteger}`;
  }

  let str = String(inputValue).trim();

  // Hapus karakter tidak valid
  str = str.replace(/[^0-9,.\-]/g, '');

  // Tentukan apakah negatif diperbolehkan
  let isNegative = isAllowNegative && str.startsWith('-');

  // Bersihkan semua minus
  str = str.replace(/-/g, '');
  if (isNegative) str = '-' + str;

  const [mainPartRaw, decimalPartRaw] = str.replace('-', '').split(',');

  const mainClean = mainPartRaw.replace(/\./g, '');
  const mainFormatted = mainClean.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  const finalFormatted = isNegative ? `-${mainFormatted}` : mainFormatted;

  if (decimalPartRaw !== undefined) {
    const limitedDecimal = decimalPartRaw.slice(0, 2);
    return `${finalFormatted},${limitedDecimal}`;
  }

  return finalFormatted;
};


export const unformatNumberInput = (value: string): number => {
  if (!value) return 0;
  const clean = value.replace(/\./g, '').replace(',', '.'); // 10.000,45 => 10000.45
  return parseFloat(clean);
};


export const returnLeadProducts = (leadProducts: ListProduct[]) => {
  if (leadProducts.length > 0) {
    return leadProducts.map((product: ListProduct) => {
      const productLine = `${product.productName} ${product.unitName ? `(${product.unitName})` : '(PCS)'}`;
      const notesLine = product.leadProductNotes && product.leadProductNotes !== '-' ? `*${product.leadProductNotes}` : '';
      return notesLine ? `${productLine}\n${notesLine}` : productLine;
    }).join(',\n')
  }
  return '-'
}

export const fetcherSWR = async ([url, headers, queries]: [string | undefined, any, Record<string, any>]) => {
  try {
    if (!url) {
      throw new Error('URL is required for fetcherSWR');
    }
    const response = await axios.get(url, {
      headers,
      params: queries,
    });

    if (!response.data.success) {
      const error: any = new Error(response.data.message);
      throw error;
    }

    return response.data.data;
  } catch (error: any) {
    throw error;
  }
};


export function getColorFromId(id: string): string {
  // Ubah ID menjadi hash numerik
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash; // Konversi ke 32-bit
  }

  // Konversi hash menjadi kode warna HEX
  let color = "#";
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += ("00" + value.toString(16)).slice(-2);
  }
  return color;
}

export const isWeekend = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.getDay();
  return day === 0 || day === 6;
}